import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { type TextareaFieldValue } from '@gorilla/portal-shared/src/schemas';
import { type TextareaFieldDescriptor } from '@gorilla/portal-shared/src/types';
import { IconButton } from '@vibe/core';
import { Apps } from '@vibe/icons';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { ExternalLink } from './ExternalLink';

const PLUGINS = ['header', 'font-bold', 'font-italic', 'list-unordered', 'list-ordered', 'block-code-inline', 'link'];

type MarkdownTextareaProps = {
  value: TextareaFieldValue;
  rows?: number;
  hideHelp?: boolean;
  label?: string;
  placeholder?: string;
  onChange: (value: TextareaFieldValue) => void;
  onShowPlaceholdersModalClick: (insertHandler?: (placeholder: string) => void) => void;
  fieldDescriptor?: TextareaFieldDescriptor;
};

export function MarkdownTextarea({
  rows,
  hideHelp,
  value,
  label,
  placeholder,
  onChange,
  onShowPlaceholdersModalClick,
  fieldDescriptor,
}: MarkdownTextareaProps) {
  const [draftText, setDraftText] = useState(() => value || '');
  const editorRef = useRef<MdEditor>(null);

  useEffect(() => {
    if (value != draftText) {
      setDraftText(value || '');
    }
  }, [value]);

  const draftTextDeferred = useDebounce(draftText, 200);

  useEffect(() => {
    if (draftTextDeferred !== value) {
      onChange(draftTextDeferred);
    }
  }, [draftTextDeferred, value]);

  const editorHeight = (rows || 5) * 24 + 38;

  const handleInsertPlaceholderClick = () => {
    if (!fieldDescriptor?.placeholdersSupport) return;

    onShowPlaceholdersModalClick((placeholder: string) => {
      if (editorRef.current) {
        editorRef.current.insertText(placeholder, false);
      } else {
        setDraftText((prev) => `${prev || ''}${placeholder}`);
      }
    });
  };

  return (
    <div className="section__field section__field--textarea gorilla-md-editor-wrapper" style={{ position: 'relative' }}>
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '22px',
            color: '#323338',
          }}
        >
          {label}
        </label>
      )}
      <MdEditor
        ref={editorRef}
        value={draftText}
        placeholder={placeholder}
        style={{ height: `${editorHeight}px` }}
        renderHTML={(text) => text}
        onChange={({ text }) => setDraftText(text)}
        view={{ menu: true, md: true, html: false }}
        canView={{ menu: true, md: true, html: true, both: false, fullScreen: false, hideMenu: false }}
        plugins={PLUGINS}
        config={{ linkUrl: 'https://example.com' }}
      />
      {fieldDescriptor?.placeholdersSupport && (
        <div
          style={{
            position: 'absolute',
            bottom: '5px',
            right: '4px',
            zIndex: 10,
          }}
        >
          <IconButton
            icon={Apps}
            size="small"
            kind="tertiary"
            onClick={handleInsertPlaceholderClick}
            className="custom__addplaceholderbutton"
          />
        </div>
      )}
      {!hideHelp && (
        <div
          style={{
            position: 'absolute',
            top: '38px',
            right: '16px',
            zIndex: 10,
          }}
        >
          <ExternalLink
            href="https://getgorilla.app/products/client-portal/docs/user-guides/text-formatting"
            target="_blank"
            style={{ fontSize: '12px', textDecoration: 'none', color: '#676879' }}
          >
            Markdown support
            <img src="/markdown.svg" width="23" height="14" alt="" style={{ margin: '0 0 0 8px', transform: 'translate3d(0, 2.5px, 0)' }} />
          </ExternalLink>
        </div>
      )}
    </div>
  );
}
